import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/buyers/login")({
	component: LoginPage,
});

function LoginPage() {
	const navigate = useNavigate();
	const { login } = useAuth();
	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState({
		email: "",
		password: "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	const validateForm = () => {
		const newErrors: Record<string, string> = {};

		if (!formData.email.trim()) {
			newErrors.email = "Email is required";
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = "Please enter a valid email address";
		}
		if (!formData.password) {
			newErrors.password = "Password is required";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) return;

		setIsLoading(true);
		try {
			const success = await login(formData.email, formData.password);

			if (success) {
				toast.success("Welcome back!");
				navigate({ to: "/solicitations" });
			} else {
				toast.error("Invalid email or password");
				setErrors({ email: "Invalid email or password" });
			}
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Invalid credentials";
			toast.error(message);
			setErrors({ email: message });
		} finally {
			setIsLoading(false);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<Card className="w-full max-w-md">
				<CardHeader className="space-y-1">
					<CardTitle className="text-2xl font-bold text-center">
						Sign in to your account
					</CardTitle>
					<CardDescription className="text-center">
						Welcome back to the Bidscope Purchasing
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4" noValidate>
						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								name="email"
								type="email"
								placeholder="john.doe@example.gov"
								value={formData.email}
								onChange={handleChange}
								aria-invalid={!!errors.email}
							/>
							{errors.email && (
								<p className="text-sm text-red-500">{errors.email}</p>
							)}
						</div>

						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<Label htmlFor="password">Password</Label>
								<a
									href="/buyers/forgot-password"
									className="text-sm text-primary underline underline-offset-4 hover:text-primary/80"
								>
									Forgot password?
								</a>
							</div>
							<Input
								id="password"
								name="password"
								type="password"
								placeholder="Enter your password"
								value={formData.password}
								onChange={handleChange}
								aria-invalid={!!errors.password}
							/>
							{errors.password && (
								<p className="text-sm text-red-500">{errors.password}</p>
							)}
						</div>

						<Button type="submit" className="w-full" disabled={isLoading}>
							{isLoading ? "Signing in..." : "Sign in"}
						</Button>
					</form>

					<div className="mt-4 text-center text-sm">
						Don't have an account?{" "}
						<a
							href="/buyers/register"
							className="text-primary underline underline-offset-4 hover:text-primary/80"
						>
							Sign up
						</a>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
